import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addSavedAddress, updateSavedAddress } from '../../../api/privateApis';
import { getProvinces } from '../../../api/shipmentForms';
import {
  AddSaveAddressInitialValues,
  AddSaveAddressValidationValues,
  editSaveAddressValidationValues,
} from '../../../initial-values';
import { setProvincesList } from '../../../redux/app/appActions';
import { RootState } from '../../../redux/rootReducer';
import { AddSaveAddressDto } from '../../../types';
import { isValidationValidated } from '../../../utils/helper';
import CountriesSelect from '../../global-components/countriesSelect';
import ProvincesSelect from '../../global-components/provincesSelect';
import { useLocation } from 'react-router-dom';
import AddressInput from '../../global-components/addressInput';
import useCountriesList from '../../../hooks/useCountriesList';
import UbbeModalHeader from '../../global-components/modalHeader';
import UbbeModalFooter from '../../global-components/modalFooter';
interface Props {
  onClose: any;
  onSuccess: any;
  editData?: any;
}
const AddSavedAddressModal = ({ onClose, onSuccess, editData }: Props) => {
  const { userData, provincesList } = useSelector(
    (state: RootState) => state.mainAppData
  );
  const countriesList = useCountriesList();
  const location = useLocation();
  const user = new URLSearchParams(location.search).get('user');
  const isVendor = new URLSearchParams(location.search).get('is_vendor');
  const account = new URLSearchParams(location.search).get('account_number');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const [savedAddressForm, setSavedAddressForm] = useState<AddSaveAddressDto>(
    editData ? editData : AddSaveAddressInitialValues
  );
  const [savedAddressFormValidation, setSavedAddressFormValidation] =
    useState<any>(
      editData
        ? editSaveAddressValidationValues
        : AddSaveAddressValidationValues
    );
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [provinces, setProvinces] = useState<any>(provincesList);
  const [isCountryChanged, setIsCountryChanged] = useState(false);
  function handleAction() {
    setValidating(true);
    if (
      isValidationValidated(savedAddressFormValidation) &&
      savedAddressForm.username !== '' &&
      savedAddressForm.account_number !== ''
    ) {
      setLoading(true);
      if (editData) {
        updateSavedAddress(editData.id, savedAddressForm)
          .then((response) => {
            responseHandle(response);
            enqueueSnackbar('Address has been updated', { variant: 'success' });
          })
          .catch((e) => {
            setLoading(false);
            setValidating(false);
          });
      } else {
        addSavedAddress(savedAddressForm)
          .then((response) => {
            responseHandle(response);
            enqueueSnackbar('Address has been added', { variant: 'success' });
          })
          .catch((e) => {
            setLoading(false);
            setValidating(false);
          });
      }
    } else {
      enqueueSnackbar('Please fill all the fields', { variant: 'error' });
    }
  }
  function responseHandle(response: any) {
    if (!response?.data?.is_error) {
      onSuccess();
      onClose();
    }
    setLoading(false);
    setValidating(false);
  }

  useEffect(() => {
    getProvinces(savedAddressForm.address.country)
      .then((data) => {
        setProvinces(data?.data?.content);
        dispatch(setProvincesList(data?.data?.content));
        if (isCountryChanged) {
          setSavedAddressForm({
            ...savedAddressForm,
            address: {
              ...savedAddressForm.address,
              province: data?.data?.content[0]?.code,
            },
          });
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }, [savedAddressForm.address.country, dispatch]);

  const handleClose = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (
      savedAddressForm.username === '' ||
      savedAddressForm.account_number === ''
    ) {
      setSavedAddressForm({
        ...savedAddressForm,
        username: user ? user : userData?.username,
        account_number: account ? account : userData?.account_number,
        is_vendor: user ? isVendor === 'true' : undefined,
      });
    }
  }, [savedAddressForm]);

  return (
    <div
      className='modal'
      id='terms_modal'
      style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5) ' }}
      tabIndex={-1}
      aria-labelledby='terms_modal_label'
      aria-hidden='true'
      onClick={handleClose}
    >
      {loading && (
        <div className='loader-container'>
          <div className='spinner'></div>
        </div>
      )}
      <div className='modal-dialog modal-md'>
        <div className='modal-content'>
          <UbbeModalHeader
            headerText='Address Details'
            onClose={onClose}
          />

          <div className='modal-body'>
            <input
              type='hidden'
              className='form-control ubbe-input'
              id='saved_address_id'
              value='-1'
            />
            <div className='mb-3'>
              <label
                className='form-label lex-18-400 color-black mb_0'
                htmlFor='name'
              >
                Full Name <span className='text-danger'>*</span>
              </label>
              <input
                className={`form-control ubbe-input ${
                  validating && savedAddressForm.name !== ''
                    ? 'is-valid'
                    : validating && savedAddressForm.name === ''
                    ? 'is-invalid'
                    : ''
                }
                ${savedAddressForm.name ? 'highlighted-border' : ''}
                `}
                data-width='100%'
                data-live-search='true'
                id='name'
                placeholder='Full Name'
                maxLength={30}
                value={savedAddressForm.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSavedAddressFormValidation({
                    ...savedAddressFormValidation,
                    name: e.target.value !== '',
                  });
                  setSavedAddressForm({
                    ...savedAddressForm,
                    name: e.target.value,
                  });
                }}
              />
              {validating && savedAddressForm.name === '' && (
                <div className='error_text'>
                  {'This field may not be blank.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label
                htmlFor='address'
                className='form-label lex-18-400 color-black mb_0'
              >
                Address <span className='text-danger'>*</span>
              </label>
              <AddressInput
                formData={savedAddressForm}
                setFormData={setSavedAddressForm}
                formValidations={savedAddressFormValidation}
                setFormValidations={setSavedAddressFormValidation}
                validating={validating}
                countriesList={countriesList}
                isNested={true}
              />
              {/* <input
                type='text'
                className={`form-control ubbe-input ${
                  validating && savedAddressForm.address.address !== ''
                    ? 'is-valid'
                    : validating && savedAddressForm.address.address === ''
                    ? 'is-invalid'
                    : ''
                } `}
                id='address'
                placeholder='Address'
                autoComplete='new-password'
                maxLength={30}
                value={savedAddressForm.address.address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSavedAddressFormValidation({
                    ...savedAddressFormValidation,
                    address: e.target.value !== '',
                  });
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      address: e.target.value,
                    },
                  });
                }}
              /> */}
              {validating && savedAddressForm.address.address === '' && (
                <div className='error_text'>
                  {'This field may not be blank.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label
                className='form-label lex-18-400 color-black mb_0'
                htmlFor='city'
              >
                City <span className='text-danger'>*</span>
              </label>
              <input
                className={`form-control ubbe-input ${
                  validating && savedAddressForm.address.city !== ''
                    ? 'is-valid'
                    : validating && savedAddressForm.address.city === ''
                    ? 'is-invalid'
                    : ''
                }
                ${savedAddressForm.address.city ? 'highlighted-border' : ''} `}
                data-width='100%'
                data-live-search='true'
                id='city'
                placeholder='City'
                maxLength={30}
                value={savedAddressForm.address.city}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSavedAddressFormValidation({
                    ...savedAddressFormValidation,
                    city: e.target.value !== '',
                  });
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      city: e.target.value,
                    },
                  });
                }}
              />
              {validating && savedAddressForm.address.city === '' && (
                <div className='error_text'>
                  {'This field may not be blank.'}
                </div>
              )}
            </div>
            <div className='mb-3'>
              <label
                className='form-label lex-18-400 color-black mb_0'
                htmlFor='country'
              >
                Country <span className='text-danger'>*</span>
              </label>
              <CountriesSelect
                onSelect={(val: any) => {
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      country: val,
                      province: '',
                    },
                  });
                  if (val) {
                    setIsCountryChanged(true);
                  }
                }}
                selectedCountry={savedAddressForm?.address?.country}
              />
            </div>
            <div className='mb-3'>
              <label
                className='form-label lex-18-400 color-black mb_0'
                htmlFor='province'
              >
                Province <span className='text-danger'>*</span>
              </label>
              <ProvincesSelect
                onSelect={(val: any) => {
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      province: val,
                    },
                  });
                }}
                provincesList={provinces}
                selectedProvince={savedAddressForm?.address.province}
              />
              <p
                className='text-danger'
                id='province_error'
              ></p>
            </div>
            <div className='mb-3'>
              <label
                className='form-label lex-18-400 color-black mb_0'
                htmlFor='postal_code'
              >
                Postal Code/Zip Code <span className='text-danger mb_0'>*</span>
              </label>
              <input
                className={`form-control ubbe-input ${
                  validating && savedAddressForm.address.postal_code !== ''
                    ? 'is-valid'
                    : validating && savedAddressForm.address.postal_code === ''
                    ? 'is-invalid'
                    : ''
                } 
                
                ${
                  savedAddressForm.address.postal_code
                    ? 'highlighted-border'
                    : ''
                }`}
                data-width='100%'
                data-live-search='true'
                id='postal_code'
                placeholder='Postal Code/Zip Code'
                maxLength={30}
                value={savedAddressForm.address.postal_code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSavedAddressFormValidation({
                    ...savedAddressFormValidation,
                    postal_code: e.target.value !== '',
                  });
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      postal_code: e.target.value,
                    },
                  });
                }}
              />
              {validating && savedAddressForm.address.postal_code === '' && (
                <div className='error_text'>
                  {'This field may not be blank.'}
                </div>
              )}
            </div>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={!savedAddressForm.address.has_shipping_bays}
                id='is_residential'
                onChange={(e) => {
                  setSavedAddressForm({
                    ...savedAddressForm,
                    address: {
                      ...savedAddressForm.address,
                      has_shipping_bays: !e.target.checked,
                    },
                  });
                }}
              />
              <label
                className='form-check-label lex-black-18-300'
                htmlFor='is_residential'
              >
                Is address residential?
              </label>
              <p
                className='text-danger'
                id='is_residential_error'
              ></p>
            </div>
            <div className='d-flex gap-2'>
              <div className='form-check'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  checked={savedAddressForm.is_origin}
                  id='is_origin'
                  onChange={(e) => {
                    setSavedAddressForm({
                      ...savedAddressForm,
                      is_origin: e.target.checked,
                    });
                  }}
                />
                <label
                  className='form-check-label lex-black-18-300'
                  htmlFor='is_origin'
                >
                  Show in origin?
                </label>
                <p
                  className='text-danger'
                  id='is_origin_error'
                ></p>
              </div>
              <div className='form-check'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  id='is_destination'
                  checked={savedAddressForm.is_destination}
                  onChange={(e) => {
                    setSavedAddressForm({
                      ...savedAddressForm,
                      is_destination: e.target.checked,
                    });
                  }}
                />
                <label
                  className='form-check-label lex-black-18-300'
                  htmlFor='is_destination'
                >
                  Show in destination?
                </label>
                <p
                  className='text-danger'
                  id='is_destination_error'
                ></p>
              </div>
            </div>
            <UbbeModalFooter
              primaryButtonText={'Save Address'}
              onSave={handleAction}
              onClose={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSavedAddressModal;
